import { fitSheetsAboveKeyboard, releaseSheets } from "./keyboardSheets";

// The keyboard and the focused field, in the phone apps. With Keyboard.resize
// = native (capacitor.config.ts) the webview SHRINKS for the keyboard instead
// of iOS panning the page — but on iOS only after the keyboard has finished
// rising, so for that moment a field low on the screen is covered.
//
// Whenever the keyboard opens, the focused field ends fully in view with
// ROOM to spare above the keyboard (founder, 1.5.2: "make sure when you click
// a field it scrolls up enough so the keyboard is not hiding it"). On iOS this
// happens at `keyboardWillShow`, which carries the keyboard's height, so the
// scroll glides up WITH the keyboard instead of after it; open bottom sheets
// are laid out for the final screen at the same moment (keyboardSheets.ts).
// It is checked again once the keyboard is up and once the webview has
// resized. The nearest scroller does the scrolling (a sheet body, the page),
// and a field already in view with room is left alone, so page-level pins
// like the song-search rise are never fought.
//
// The events come from the Capacitor Keyboard plugin on `window` itself:
// nothing to import, nothing to await. On the web they never fire.

/** Clear space kept between the focused field and the keyboard's top edge. */
export const KEYBOARD_ROOM = 24;
/** …and between the field and the top of whatever it scrolls in. */
const TOP_ROOM = 16;

let installed = false;

const isField = (el: Element | null): el is HTMLElement =>
  !!el &&
  ((el instanceof HTMLInputElement && !["checkbox", "radio", "range", "file", "button", "submit"].includes(el.type)) ||
    el instanceof HTMLTextAreaElement ||
    (el as HTMLElement).isContentEditable);

function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

/** Scrolls the focused field so it sits fully above `visibleBottom` with room. */
export function revealFocusedField(visibleBottom: number, smooth = false): void {
  const el = document.activeElement;
  if (!isField(el)) return;
  const r = el.getBoundingClientRect();
  const scroller = scrollerOf(el);
  const box = scroller?.getBoundingClientRect();
  const top = Math.max(box?.top ?? 0, 0) + TOP_ROOM;
  const bottom = Math.min(box?.bottom ?? visibleBottom, visibleBottom) - KEYBOARD_ROOM;
  let delta = r.bottom > bottom ? r.bottom - bottom : 0;
  if (r.top - delta < top) delta = r.top - top; // a field taller than the room: its top wins
  if (Math.abs(delta) < 1) return;
  const behavior: ScrollBehavior = smooth && !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "smooth" : "auto";
  // Absolute targets, not scrollBy: a second check while the first smooth
  // scroll is still running then aims at the same place instead of adding
  // its delta on top (WebKit stacks scrollBy onto a running animation).
  if (scroller) scroller.scrollTo({ top: scroller.scrollTop + delta, behavior });
  else if (delta > 0) window.scrollTo({ top: window.scrollY + delta, behavior }); // the page only ever scrolls a covered field UP
}

type KeyboardEvt = Event & { keyboardHeight?: number };

/** `fitSheets`: lay bottom sheets out for the keyboard as it opens (iOS — the
    webview shrinks only after the keyboard has risen there). Android, not
    measured, keeps the plain reveal after its resize. */
export function installKeyboardReveal({ fitSheets = true }: { fitSheets?: boolean } = {}): () => void {
  if (installed) return () => {};
  installed = true;
  const listeners: [string, (e: KeyboardEvt) => void][] = [];
  const on = (name: string, fn: (e: KeyboardEvt) => void) => {
    listeners.push([name, fn]);
    window.addEventListener(name, fn);
  };
  // The webview's height with the keyboard down; the keyboard's height while up.
  let fullHeight = window.innerHeight;
  let keyboard = 0;
  const visible = () => {
    const now = Math.min(window.innerHeight, window.visualViewport?.height ?? Infinity);
    return keyboard && fitSheets ? Math.min(now, fullHeight - keyboard) : now;
  };

  let cancelPending: (() => void) | null = null;
  const onceOnResize = (fn: () => void) => {
    cancelPending?.();
    const run = () => {
      cancel();
      fn();
    };
    const timer = setTimeout(run, 900);
    const cancel = () => {
      window.removeEventListener("resize", run);
      clearTimeout(timer);
      cancelPending = null;
    };
    cancelPending = cancel;
    window.addEventListener("resize", run);
  };

  on("resize", () => {
    if (!keyboard) fullHeight = window.innerHeight;
  });

  on("keyboardWillShow", (e: KeyboardEvt) => {
    const h = Number(e.keyboardHeight) || 0;
    if (!fitSheets || h <= 0) return;
    cancelPending?.(); // a close that is being reopened (field → picker → field): the sheets stay fitted
    // A first open (the webview still full height) or a field change with
    // the keyboard already up (already shrunk): fullHeight is the screen either way.
    keyboard = h;
    fitSheetsAboveKeyboard(fullHeight - h);
    revealFocusedField(visible(), true);
  });

  // Measured in the simulator: keyboardDidShow arrives BEFORE the webview
  // shrinks (the window `resize` lands a beat later). Check now and once
  // more on that resize, with a fallback in case none comes.
  on("keyboardDidShow", (e: KeyboardEvt) => {
    const h = Number(e.keyboardHeight) || 0;
    if (fitSheets && h > 0 && h !== keyboard) {
      keyboard = h;
      fitSheetsAboveKeyboard(fullHeight - h);
    }
    revealFocusedField(visible());
    onceOnResize(() => revealFocusedField(visible()));
  });

  // Sheets stay laid out for the keyboard until the webview has grown back
  // (a field-to-field hop, which can post hide-then-show, cancels this).
  on("keyboardWillHide", () => {
    if (!keyboard) return;
    keyboard = 0;
    onceOnResize(releaseSheets);
  });

  // Uninstall (tests; the app installs once for its whole life).
  return () => {
    cancelPending?.();
    listeners.forEach(([name, fn]) => window.removeEventListener(name, fn));
    installed = false;
  };
}
